'use client'

import { FC, TouchEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Panel, PanelItem, PanelLayout } from '../Panel'
import styles from './styles.module.scss'
import { BodySize, Colour, SectionPaddingDefault } from '../constants'
import cx from 'classnames'
import { Link } from '../Link'
import { CmsQuoteCard, QuoteCard } from '@/components/QuoteCard/QuoteCard'
import { IconographyButton } from '@/components/IconographyButton/IconographyButton'
import arrowRight from '@/assets/icons/arrow-right-white.svg'
import arrowLeft from '@/assets/icons/arrow-left-white.svg'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { CmsPageSectionBase } from '@/components/ComposedPage'
import { CmsCardPanelCard } from '@/components/CardPanel/CardPanelTypes'
import { Card } from '@/components/Card/Card'
import {
  BlogPostCard,
  BlogPostCardProps,
} from '@/components/Blog/BlogHome/BlogPostCard'
import { useIsClient } from '@mettle/hooks'
import {
  LandingPageCard,
  LandingPageCardProps,
} from '@/components/LandingPageCard/LandingPageCard'

export enum CompatibleCarouselItemsType {
  QuoteCard = 'quoteCard',
  BlogPost = 'blogPost',
  Card = 'cardPanelCard',
  LandingPageCard = 'landingPage',
}

type WithCarouselItemType<T, K extends CompatibleCarouselItemsType> = T & {
  sys: {
    id?: string
    contentType: {
      sys: {
        id: K
      }
    }
  }
}

type CarouselItemMap = {
  [CompatibleCarouselItemsType.QuoteCard]: WithCarouselItemType<
    CmsQuoteCard,
    CompatibleCarouselItemsType.QuoteCard
  >
  [CompatibleCarouselItemsType.BlogPost]: WithCarouselItemType<
    BlogPostCardProps,
    CompatibleCarouselItemsType.BlogPost
  >
  [CompatibleCarouselItemsType.Card]: WithCarouselItemType<
    CmsCardPanelCard,
    CompatibleCarouselItemsType.Card
  >
  [CompatibleCarouselItemsType.LandingPageCard]: WithCarouselItemType<
    LandingPageCardProps,
    CompatibleCarouselItemsType.LandingPageCard
  >
}

export type CarouselItemType = CarouselItemMap[keyof CarouselItemMap]

export const CarouselItemTypeMap = {
  [CompatibleCarouselItemsType.QuoteCard]: QuoteCard,
  [CompatibleCarouselItemsType.BlogPost]: BlogPostCard,
  [CompatibleCarouselItemsType.Card]: Card,
  [CompatibleCarouselItemsType.LandingPageCard]: LandingPageCard,
} satisfies {
  [K in CompatibleCarouselItemsType]: FC<
    CarouselItemMap[K] & { inCarousel?: boolean }
  >
}

export interface CmsPageCarouselSection extends CmsPageSectionBase {
  fields: CmsPageSectionBase['fields'] & {
    title?: string
    subtitle?: string
    linkText?: string
    linkUrl?: string
    centerTitles?: boolean
    backgroundColour: Colour
    displayItems: number
    showControls: boolean
    autoScroll: boolean
    autoScrollInterval?: number
    items: CarouselItemType[]
  }
}

interface CarouselState {
  carouselPosition: number
  paneCount: number
  itemsPerPane: number
  autoScrollDisabled: boolean
  forwardButtonDisabled: boolean
  backwardButtonDisabled: boolean
  flexStyle: string
  width: number
}

export const PageCarousel: FC<CmsPageCarouselSection> = ({ fields }) => {
  const {
    title,
    subtitle,
    linkText,
    linkUrl,
    centerTitles,
    backgroundColour,
    items,
    displayItems,
    showControls,
    autoScroll,
    autoScrollInterval = 1000,
    sectionPaddingTop,
    sectionPaddingBottom,
  } = fields
  const [
    {
      carouselPosition,
      paneCount,
      itemsPerPane,
      autoScrollDisabled,
      forwardButtonDisabled,
      backwardButtonDisabled,
      flexStyle,
      width,
    },
    setCarouselState,
  ] = useState<CarouselState>({
    carouselPosition: 0,
    paneCount: items.length,
    itemsPerPane: displayItems,
    autoScrollDisabled: !autoScroll,
    forwardButtonDisabled: false,
    backwardButtonDisabled: true,
    flexStyle: '',
    width: 0,
  })

  const isClient = useIsClient()
  const carouselRef = useRef(null)
  const [touchStartX, setTouchStartX] = useState(0)
  const isInView = useIntersectionObserver(carouselRef, true, 0.9, true)

  const touchStart = useCallback((evt: TouchEvent<HTMLDivElement>) => {
    const touchStartClientX = evt.changedTouches[0]?.clientX
    if (touchStartClientX) {
      setTouchStartX(touchStartClientX)
    }
  }, [])

  const touchEnd = useCallback(
    (evt: TouchEvent<HTMLDivElement>) => {
      const touchEndClientX = evt.changedTouches[0]?.clientX
      if (touchEndClientX !== undefined) {
        const deviation = 30 // Detecting actual left or right swipe
        if (touchEndClientX >= touchStartX + deviation) {
          handleCarouselBackward()
        }
        if (touchEndClientX <= touchStartX - deviation) {
          handleCarouselForward()
        }
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [touchStartX],
  )

  const willTransitionToLastPane = (): boolean => {
    return items.length - carouselPosition * itemsPerPane < itemsPerPane
  }
  const calculateMarginLeft = () => {
    if (carouselPosition) {
      if (willTransitionToLastPane()) {
        return `-${
          carouselPosition * 100 -
          (100 - ((items.length % itemsPerPane) / itemsPerPane) * 100)
        }%`
      } else {
        return `-${carouselPosition}00%`
      }
    }

    return 0
  }

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    if (autoScroll && !autoScrollDisabled && isInView) {
      timeout = setTimeout(() => {
        if (carouselPosition < paneCount - 1) {
          setCarouselState(prevState => {
            return {
              ...prevState,
              carouselPosition: prevState.carouselPosition + 1,
              forwardButtonDisabled: carouselPosition + 1 === paneCount - 1,
              backwardButtonDisabled: false,
            }
          })
        }
      }, autoScrollInterval)
    }

    return () => clearTimeout(timeout)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselPosition, autoScrollDisabled, autoScroll, paneCount, isInView])

  const handleResize = () => {
    const newWidth = window.innerWidth
    const resetState = {
      carouselPosition: 0,
      forwardButtonDisabled: false,
      backwardButtonDisabled: true,
      width: newWidth,
      autoScrollDisabled: false,
    }

    if (Math.abs(width - newWidth) > 30) {
      if (newWidth > BodySize.maxTablet) {
        setCarouselState(prevState => {
          return {
            ...prevState,
            ...resetState,
            paneCount: Math.ceil(items.length / displayItems),
            itemsPerPane: displayItems,
            flexStyle: styles[`itemFlex${displayItems}`],
          }
        })
      } else if (
        newWidth >= BodySize.maxMobile &&
        newWidth <= BodySize.maxTablet
      ) {
        setCarouselState(prevState => {
          return {
            ...prevState,
            ...resetState,
            paneCount: Math.ceil(items.length / Math.min(2, displayItems)),
            itemsPerPane: Math.min(2, displayItems),
            flexStyle: styles[`itemFlex${Math.min(2, displayItems)}`],
          }
        })
      } else {
        setCarouselState(prevState => {
          return {
            ...prevState,
            ...resetState,
            paneCount: items.length,
            itemsPerPane: 1,
            flexStyle: '',
          }
        })
      }
    } else {
      setCarouselState(prevState => {
        return {
          ...prevState,
          width: newWidth,
        }
      })
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize)
    }
    return () => window.removeEventListener('resize', handleResize)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [width])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      handleResize()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCarouselForward = () => {
    if (carouselPosition < paneCount - 1) {
      setCarouselState(prevState => {
        return {
          ...prevState,
          carouselPosition: carouselPosition + 1,
          autoScrollDisabled: true,
          forwardButtonDisabled: carouselPosition + 1 === paneCount - 1,
          backwardButtonDisabled: false,
        }
      })
    }
  }

  const handleCarouselBackward = () => {
    if (carouselPosition > 0) {
      setCarouselState(prevState => {
        return {
          ...prevState,
          carouselPosition: carouselPosition - 1,
          autoScrollDisabled: true,
          forwardButtonDisabled: false,
          backwardButtonDisabled: carouselPosition - 1 === 0,
        }
      })
    }
  }

  const isActivePane = (index: number) => index === carouselPosition

  return (
    <Panel
      data-testid="Carousel"
      layout={PanelLayout.full}
      colour={Colour[backgroundColour]}
      className={cx(
        styles.carouselPanel,
        styles[sectionPaddingTop || SectionPaddingDefault.TOP],
        styles[sectionPaddingBottom || SectionPaddingDefault.BOTTOM],
      )}
      short
    >
      <PanelItem className={styles.carousel}>
        <div
          className={cx(styles.context, {
            [styles.centerTitles]: Boolean(centerTitles),
          })}
        >
          {title && <h4 className={styles.title}>{title}</h4>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          {linkText && linkUrl && (
            <Link className={styles.link} url={linkUrl}>
              {linkText}
            </Link>
          )}
        </div>
        <div className={styles.carouselContainer} ref={carouselRef}>
          <div
            id="animated"
            className={cx(styles.animated)}
            style={{
              marginLeft: calculateMarginLeft(),
            }}
            onTouchStart={touchStart}
            onTouchMove={touchEnd}
          >
            {isClient &&
              items.map((item, index) => {
                // Extract discriminator: check nested post first (for BlogPostCardProps),
                // then fall back to direct item sys (for QuoteCard and Card)
                const itemType = (('post' in item &&
                  item.post &&
                  'sys' in item.post &&
                  item.post.sys?.contentType?.sys?.id) ||
                  item.sys?.contentType?.sys?.id) as CompatibleCarouselItemsType

                if (!itemType) {
                  return null
                }

                const Component = CarouselItemTypeMap[itemType] as FC<
                  typeof item & { inCarousel?: boolean }
                >

                if (!Component) {
                  return null
                }

                return (
                  <div
                    key={item.sys?.id || index}
                    className={cx(styles.item, flexStyle)}
                  >
                    <Component {...item} inCarousel />
                  </div>
                )
              })}
          </div>
        </div>
        <div className={styles.controlsContainer}>
          {!showControls && (
            <div className={styles.indicators}>
              {Array(paneCount)
                .fill('')
                .map((_, index) => (
                  <div
                    key={index}
                    className={cx(styles.indicator, {
                      [styles.active]: isActivePane(index),
                    })}
                  ></div>
                ))}
            </div>
          )}
          {showControls && paneCount > 1 && (
            <div className={styles.controls}>
              <IconographyButton
                value="Back"
                hollow
                icon={arrowLeft}
                handler={handleCarouselBackward}
                disabled={backwardButtonDisabled}
              />
              <IconographyButton
                value="Forward"
                icon={arrowRight}
                handler={handleCarouselForward}
                disabled={forwardButtonDisabled}
              />
            </div>
          )}
        </div>
      </PanelItem>
    </Panel>
  )
}
